import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AnimatePresence, LayoutGroup } from 'framer-motion';
import { Loader } from 'lucide-react';
import FileListItem from './FileListItem';
import { RootState } from '../../../../reducers';
import { fetchFiles, toggleSelectFile } from '../../librarySlice';
import FilesSkeleton from './FilesSkeleton';
import { RouteParams } from '../../types';
import { getFileUrlForUser } from '../../../../lib/routingUtils';
import PendingUploads from '../../../upload/PendingUploads';
import FilesEmpty from '../FilesEmpty';
import { useQuery } from '../../../../hooks/urlHooks';
import FileListHeader from './FileListHeader';
import { useSelectMode } from '../../context/SelectModeContext';
import { FileType } from '../../../../types';
import useDebounce from '../../../../hooks/useDebounce';
import RecoveryFileListItem from '../RecoveryFileListItem';
import { loadBackupRecordsThunk } from '../../../settings/settingsSlice';
import { Table, TableBody, TableHeader } from '../../../../components/ui/table';
import { useMediaQuery } from '../../../../hooks/use-media-query';

export const FileListView = React.memo(() => {
  const {
    library,
    upload,
    import: importState,
    settings,
  } = useSelector((state: RootState) => state);
  const { activeUploads } = upload;
  const currentUser = settings.user;
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const { searchQuery, sortBy = 'createdAt', sortOrder = 'desc' } = useQuery();
  const { files, recentlyAddedFileIds, deletingFileIds } = library;
  const { selectedFiles } = library;
  const { selectMode, setSelectMode } = useSelectMode();
  const { status } = files;

  // Track if we should animate layout (only when files are added/removed, not on sort/search)
  const shouldAnimateLayout =
    recentlyAddedFileIds.length > 0 || deletingFileIds.length > 0;
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingPage, setLoadingPage] = useState<number | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());

  // Mobile detection moved to parent
  const isMobile = useMediaQuery('(max-width: 640px)');

  // Reduce debounce time for faster preloading
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);

  const onFileSelect = useCallback(
    (file: FileType) => {
      const isCurrentlySelected = !!selectedFiles[file._id];

      // If not in select mode, enter it first
      if (!selectMode) {
        setSelectMode(true);
      }
      dispatch(toggleSelectFile(file));

      // If chat panel is open, add or remove file from chat context
      if ((window as any).__chatPanelControls?.isOpen?.()) {
        if (isCurrentlySelected) {
          // File is being deselected, remove from chat
          (window as any).__chatContextControls?.removeFileFromContext?.(
            file._id,
          );
        } else {
          // File is being selected, add to chat
          (window as any).__chatContextControls?.addFileToContext?.(file);
        }
      }
    },
    [dispatch, selectMode, setSelectMode, selectedFiles],
  );

  const handleScroll = useCallback(
    (event?: Event) => {
      // Try to find the scrollable container - could be window or a custom container
      const { documentElement } = document;
      let { scrollHeight, scrollTop, clientHeight } = documentElement;

      // If we have an event, try to use the target if it's scrollable
      if (event && event.target && event.target !== document) {
        const target = event.target as Element;
        if (target.scrollHeight > target.clientHeight) {
          ({ scrollHeight, scrollTop, clientHeight } = target);
        }
      }

      // Also check for common scrollable containers (including MUI Box) - OPTIMIZED: Only query once if possible or rely on specific class
      // For now, simplifying to just window and the main known container if it exists
      // Finding scrollable container by ID or simpler class is better than scanning all styles
      const scrollableContainer = document.getElementById(
        'main-scroll-container',
      );

      if (scrollableContainer) {
        ({ scrollHeight, scrollTop, clientHeight } = scrollableContainer);
      } else if (scrollHeight <= clientHeight) {
        // Fallback to detecting the first scrollable element among common candidates (only if window isn't scrolling)
        // This is still expensive but safer than searching *all* elements
        const candidate = document.querySelector(
          '.overflow-y-auto, .overflow-auto',
        );
        if (candidate) {
          ({ scrollHeight, scrollTop, clientHeight } = candidate);
        }
      }

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Preload when user is 2 viewport heights away from bottom for seamless scrolling
      const preloadThreshold = window.innerHeight * 2;
      // Show skeleton only when very close to bottom (half viewport)
      const skeletonThreshold = window.innerHeight * 0.5;

      // Update near-bottom state for skeleton display (much closer to bottom)
      setIsNearBottom(distanceFromBottom < skeletonThreshold);

      if (
        distanceFromBottom < preloadThreshold &&
        files.status !== 'pending' &&
        !isLoadingMore &&
        files.pagination.total > files.data.length
      ) {
        const nextPage = files.pagination.page + 1;
        const maxPages = Math.ceil(
          files.pagination.total / files.pagination.limit,
        );

        // Prevent duplicate page requests - only load if we haven't already loaded this page
        if (
          loadingPage === nextPage ||
          nextPage > maxPages ||
          preloadedPages.has(nextPage)
        ) {
          return;
        }

        // Trigger the debounced load instead of loading immediately
        setShouldLoadMore(true);
      } else {
        setShouldLoadMore(false);
      }
    },
    [files, isLoadingMore, loadingPage],
  );

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore) {
      const nextPage = files.pagination.page + 1;
      const maxPages = Math.ceil(
        files.pagination.total / files.pagination.limit,
      );

      if (
        loadingPage !== nextPage &&
        nextPage <= maxPages &&
        !preloadedPages.has(nextPage)
      ) {
        setIsLoadingMore(true);
        setLoadingPage(nextPage);
        setPreloadedPages((prev) => new Set([...prev, nextPage]));
        dispatch(
          fetchFiles({
            teamId,
            folderId,
            page: nextPage,
            limit: files.pagination.limit, // Use the same limit as in pagination
            searchQuery,
            sortBy,
            sortOrder,
            ...(folderId === 'private' && { includeAllTeams: 'true' }),
          }),
        );
      }
    }
  }, [
    debouncedShouldLoadMore,
    files,
    dispatch,
    teamId,
    folderId,
    searchQuery,
    sortBy,
    sortOrder,
    isLoadingMore,
    loadingPage,
  ]);

  useEffect(() => {
    // Reset loading state when dependencies change
    setIsLoadingMore(false);
    setLoadingPage(null);
    setPreloadedPages(new Set());
  }, [teamId, folderId, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    // Reset loading state when files fetch completes
    if (files.status !== 'pending') {
      setIsLoadingMore(false);
      setLoadingPage(null);
    }
  }, [files.status]);

  // Load backup records for recovery detection
  useEffect(() => {
    dispatch(loadBackupRecordsThunk());
  }, [dispatch]);

  useEffect(() => {
    // Store the handler reference for cleanup
    scrollHandlerRef.current = handleScroll;

    // Listen for scroll on window/document
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Listen on specific known scroll container
    const scrollableContainer =
      document.getElementById('main-scroll-container') ||
      document.querySelector('.overflow-y-auto');
    if (scrollableContainer) {
      scrollableContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', handleScroll);
      }

      // Also remove with the ref in case the handler changed
      if (scrollHandlerRef.current) {
        window.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [handleScroll]);

  // Auto-load more items when content doesn't fill the viewport (e.g., 4K screens)
  useEffect(() => {
    let rafId: number | undefined;

    if (
      files.status === 'succeeded' &&
      !isLoadingMore &&
      files.pagination.total > files.data.length
    ) {
      rafId = requestAnimationFrame(() => {
        const scrollContainer =
          document.getElementById('main-scroll-container') ||
          document.querySelector('.overflow-y-auto');
        const target = scrollContainer || document.documentElement;
        const { scrollHeight, clientHeight } = target;

        if (scrollHeight <= clientHeight) {
          handleScroll();
        }
      });
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    files.status,
    files.data.length,
    files.pagination.total,
    isLoadingMore,
    handleScroll,
  ]);

  if (!teamId || !folderId) {
    return null;
  }

  // Show skeleton on initial load or when fetching
  const showSkeleton = status === 'pending';

  // Only show empty state after successful load with no results
  if (
    status === 'succeeded' &&
    files.data?.length === 0 &&
    activeUploads.length === 0 &&
    importState.status !== 'pending'
  ) {
    return <FilesEmpty />;
  }

  // Show skeleton while loading (prevents empty state flash on initial load)
  if (status === 'pending' && files.data.length === 0) {
    return (
      <div className="w-full">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/40">
            <FileListHeader isLoading />
          </TableHeader>
          <TableBody>
            <FilesSkeleton />
          </TableBody>
        </Table>
      </div>
    );
  }
  const showPaginationLoader =
    isLoadingMore &&
    isNearBottom &&
    files.pagination.total > files.data.length &&
    status !== 'pending';

  return (
    <LayoutGroup>
      <div className="w-full">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/40">
            <FileListHeader isLoading={status === 'pending'} />
          </TableHeader>
          <TableBody>
            <PendingUploads view="list" />
            <RecoveryFileListItem />
            <AnimatePresence mode="popLayout" initial={false}>
              {files.data.map((file) => {
                const isSelected = !!selectedFiles[file._id];
                return (
                  <FileListItem
                    file={file}
                    isSelected={isSelected}
                    selectMode={selectMode}
                    onSelect={onFileSelect}
                    fileUrl={getFileUrlForUser({
                      teamId,
                      parentId: file.parentId,
                      fileId: file._id,
                      ownerId: file.ownerId,
                      currentUserId: currentUser?._id,
                      context: 'library',
                      withBasePath: false,
                    })}
                    isMobile={isMobile}
                    key={file._id}
                    shouldAnimateLayout={shouldAnimateLayout}
                  />
                );
              })}
            </AnimatePresence>
            {showSkeleton && <FilesSkeleton />}
          </TableBody>
        </Table>
        {showPaginationLoader && (
          <div className="flex items-center justify-center py-6 border-t border-border/40">
            <Loader className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </LayoutGroup>
  );
});

export default FileListView;
